//Nov13 FormActionsContext.js:
//Oct14 FormActionsContext.js:

import React, { createContext, useContext, useState } from 'react';
import {
  submitFormData,
  getFormSubmissions,
  processSubmitActions, 
} from '../utils/firestore';
import { useFormContext } from './FormContext';
import { useFieldEdit } from './FieldEditContext';
import { useFormLayout } from './FormLayoutContext';
import { useFormSubmissionSettings } from './FormSubmissionSettingsContext'; // Import FormSubmissionSettingsContext
import { useFieldMetadata } from './FieldMetadataContext';
import { replaceChipsWithValues } from "../utils/formUtils";
import { evaluateAllCalculatedFields } from "../utils/calculatedFieldUtils";
import { FIELD_TYPE_CALCULATED } from "../components/Fields/constants/fieldTypes";
import { useMultiAnswerField } from "./MultiAnswerFieldContext"; //  Get dropdown options from here
import { applyTransformations } from "../utils/transformationUtils"; //  Import transformation logic
import { v4 as uuidv4 } from 'uuid'; 
import { useFormAnswers } from './FormAnswersContext'; // ← canonical answers (UFI) builder

/* ────────────────────────────────────────────────────────────────
 * Enterprise bulk engine (reusable)
 * ----------------------------------------------------------------
 * This is a generic “choice expansion” engine used by:
 *   • urlField CSV (enabled now when value contains commas)
 *   • multi-select (we’ll enable next step using the same engine)
 *
 * This DOES NOT change any field UI components.
 * It runs at submit-time, like other enterprise rules (Other, encode, blank policy).
 * ---------------------------------------------------------------- */
const MAX_BULK_ROWS = 200;

// Feature gating:
// - We activate CSV bulk now when a urlField contains commas.
// - Multi-select bulk will use the same engine; we’ll enable it later.
const ENABLE_BULK_URL_CSV = true;
const ENABLE_BULK_MULTISELECT = true;

/**
 * URL validation logic (copied from URLFieldComponent for parity).
 * IMPORTANT: We validate at submit-time to ensure correctness even if UI blur didn’t run.
 */
const validateURL = (url) => {
  const urlRegex = /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})$/;
  return urlRegex.test(url);
};

/**
 * Split comma-separated strings into ordered unique tokens.
 * Returns null if the input is NOT a CSV-like string (no comma).
 */
const splitCsv = (input) => {
  const raw = String(input ?? '');
  if (!raw.includes(',')) return null;

  const parts = raw
    .split(',')
    .map(s => String(s ?? '').trim())
    .filter(Boolean);

  // remove duplicates but keep order
  const seen = new Set();
  const out = [];
  for (const p of parts) {
    if (!seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  }
  return out.length ? out : null;
};

/**
 * Determine the “choice list” for a given field.
 * If it returns null, the field is treated as scalar (single choice).
 *
 * NOTE:
 * - URL CSV bulk is enabled now, but only triggers when there is a comma.
 * - Multi-select bulk is implemented here but gated off for now.
 */
const getChoiceListForField = (field, meta) => {
  const type = meta?.type || field?.type || '';
  const value = field?.value;

  // 1) urlField CSV expansion (enabled now for your initial test)
  if (ENABLE_BULK_URL_CSV && type === 'urlField') {
    const csv = splitCsv(value);
    if (!csv) return null; // not bulk → scalar
    return {
      kind: 'urlCsv',
      choices: csv, // array of URL strings
    };
  }

// 2) multi-select expansion (same engine; enabled later)
if (ENABLE_BULK_MULTISELECT && type === 'multi-select') {
  const isBulk = meta?.multiSelectResultFormat === 'bulk';
  if (!isBulk) return null;

  if (Array.isArray(value) && value.length > 0) {
    return {
      kind: 'multiSelect',
      choices: value, // array of labels at this point (post-transform)
    };
  }
}

  return null;
};

/**
 * Expand submissionData into bulk rows using cartesian product
 * across any “multi-choice” fields.
 *
 * Returns:
 *  { rows, multiFieldIds, total }
 * Or:
 *  null when no bulk fields detected.
 */
const expandSubmissionToBulkRows = (submissionData, fieldsMeta, submissionId) => {
  const multi = [];

  // Identify multi-choice fields
  for (const f of (submissionData || [])) {
    const meta = fieldsMeta?.[f.id] || {};
    const res = getChoiceListForField(f, meta);
    if (res && Array.isArray(res.choices) && res.choices.length > 0) {
      multi.push({
        fieldId: f.id,
        ufi: f.ufi,
        label: f.label,
        kind: res.kind,
        choices: res.choices,
      });
    }
  }

  if (multi.length === 0) return null;

  // Compute total combinations (product of choice lengths)
  const total = multi.reduce((acc, m) => acc * (m.choices?.length || 1), 1);

  return { multi, total };
};

/**
 * Actually build the row variants.
 * Currently only used when we detect URL CSV (and later multi-select).
 */
const buildBulkRows = (submissionData, fieldsMeta, submissionId) => {
  const detected = expandSubmissionToBulkRows(submissionData, fieldsMeta, submissionId);
  if (!detected) return { bulkRows: null };

  const { multi, total } = detected;

  // Cap
  if (total > MAX_BULK_ROWS) {
    return {
      bulkRows: null,
      error: `This submission would generate ${total} rows, which exceeds the limit of ${MAX_BULK_ROWS}.`,
      errorFieldId: multi?.[0]?.fieldId || null,
    };
  }

  // Build cartesian product rows iteratively
  // Start with one “seed” row
  let rows = [
    {
      rowIndex: 0,
      clientRowId: `local::${submissionId}::0`,
      submissionData: submissionData.map(f => ({ ...f })),
    }
  ];

  // For each multi-choice field, expand existing rows
  for (const m of multi) {
    const next = [];
    let idx = 0;

    for (const r of rows) {
      for (const choice of m.choices) {
        const cloned = r.submissionData.map(f => {
          if (f.id === m.fieldId) return { ...f, value: choice };
          return { ...f };
        });

        next.push({
          rowIndex: idx,
          clientRowId: `local::${submissionId}::${idx}`,
          submissionData: cloned,
        });
        idx += 1;
      }
    }

    rows = next;

    // Additional guard (defensive, should not hit because we capped earlier)
    if (rows.length > MAX_BULK_ROWS) {
      return {
        bulkRows: null,
        error: `This submission would generate more than ${MAX_BULK_ROWS} rows.`,
        errorFieldId: m.fieldId,
      };
    }
  }

  return { bulkRows: rows };
};

/** Force-blur any focused editor and yield one frame so onBlur → setAnswer lands.
 *  Minimal overhead; avoids write-on-change typing lag while ensuring latest text is saved. */
async function forceBlurAndWaitFrame() {
  try {
    if (typeof document !== 'undefined') {
      const active = document.activeElement;
      if (active && typeof active.blur === 'function') active.blur();
      // Nudge inputs/CEs that didn’t emit blur (e.g., MUI popper focus)
      const nodes = document.querySelectorAll('input, textarea, [contenteditable="true"]');
      nodes.forEach((el) => { try { el.dispatchEvent(new Event('blur', { bubbles: true })); } catch (_) {} });
    }
    await new Promise((r) => requestAnimationFrame(() => r())); // allow React to commit blur → setAnswer
  } catch (_) {}
}


const FormActionsContext = createContext();

export const useFormActions = () => useContext(FormActionsContext);

export const FormActionsProvider = ({ children }) => {
  const { formId, setIsFormValid } = useFormContext();
  const { fieldChanges, validationErrors, updateValidationError, clearValidationErrors } = useFieldEdit(); // Replace setValidationErrors with updateValidationError
  const { fieldsMetadata: rawFieldsMetadata } = useFieldMetadata();
  const { dropdownOptions } = useMultiAnswerField(); 

  const { layoutConfig } = useFormLayout();

  // Single hook call (prevents double-hook usage + aligns with current provider value shape)
  const {
    submissionSettings,
    updateSettings,
    activeActionType,
    isLoading: submissionSettingsLoading,
  } = useFormSubmissionSettings(); // Import from FormSubmissionSettingsContext

  const { buildSubmissionArray, getOtherText } = useFormAnswers() || {}; // ← include other text channel for "__other__"

  const [submitStatus, setSubmitStatus] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionErrors, setActionErrors] = useState([]);

  const getDataFromStorage = (key) => {
    const sessionData = sessionStorage.getItem(key);
    const localData = localStorage.getItem(key);
    return sessionData ? JSON.parse(sessionData) : localData ? JSON.parse(localData) : null;
  };

  const filterOutSubmitButton = (data) => {
    return data.filter(field => field.type !== 'submit');
  };

  /**
   * Ensure fieldsMetadata is properly extracted before use.
   */
  const getNormalizedFieldsMetadata = () => {
    console.log("[FormActionsContext: getNormalizedFieldsMetadata] Raw fieldsMetadata:", rawFieldsMetadata);
    const normalizedMetadata = rawFieldsMetadata?.value || rawFieldsMetadata || {};
    console.log("[FormActionsContext: getNormalizedFieldsMetadata] Normalized fieldsMetadata:", normalizedMetadata);
    return normalizedMetadata;
  };



  /**
   * Replace placeholder chips in HTML with actual user-submitted values.
   * Uses the utility function to ensure consistent logic.
   */
  const replaceChipsWithAnswers = (htmlString, fieldValues) => {
    console.log('[FormActionsContext] replaceChipsWithAnswers called. Original HTML:', htmlString);
    console.log('[FormActionsContext] fieldValues:', fieldValues);

    // ✅ Use the shared utility function with "final" mode
    const processedHTML = replaceChipsWithValues(htmlString, fieldValues, "final");

    console.log('[FormActionsContext] Final HTML after chip replacement:', processedHTML);
    return processedHTML;
  };





const handleAction = async (action, submissionData) => {
  if (!action.enabled) {
    console.log(`[FormActionsContext] Skipping disabled action: ${action.type}`);
    return;
  }

  console.log(`[FormActionsContext] Processing action: ${action.type}`);

  switch (action.type) {
    case 'redirect':
//      console.log(`[FormActionsContext] Redirecting to: ${action.targetUrl}`);
      if (action.targetUrl) {
        window.location.href = action.targetUrl;
      } else {
        console.error('[FormActionsContext] Redirect URL is not defined.');
      }
      break;

    case 'sendEmail':
//      console.log(`[FormActionsContext] Sending email to: ${submissionData[action.recipientField]}`);
      break;

    case 'displayToast':
//      console.log(`[FormActionsContext] Displaying toast message: ${action.message}`);
      // Trigger a toast notification logic here.
      break;
    case "displayInCard":
      console.log("[FormActionsContext] Processing displayInCard action.");
      console.log("[FormActionsContext] Submission Data before transformation:", submissionData);

      if (action.customMessage) {
        console.log("[FormActionsContext] Preparing displayInCard template with latest field answers.");

        // Build a two-key map so templates can resolve either data-ufi or data-field-id chips
        const transformedFieldValues = submissionData.reduce((acc, field) => {
          if (!field.ufi) {
            console.error(`[FormActionsContext] Missing UFI for field:`, field);
          }
          const v = field.value ?? "";
          if (field.ufi) acc[field.ufi] = v;     // UFI key
          if (field.id)  acc[field.id]  = v;     // fieldId key
          return acc;
        }, {});

        console.log("[FormActionsContext] Transformed fieldValues (UFI -> Answer):", transformedFieldValues);

        // Instead of processing the template now, store the original rich text template (with placeholders)
        // and the latest submitted answers. DisplayCardPreview will handle chip replacement at render time.
        updateSettings("displayInCard", { 
          customMessage: action.customMessage, // Store the original template
          submittedValues: transformedFieldValues // Store the current submitted values
        });
        console.log("[FormActionsContext: updateSettings] Updated displayInCard settings with original template and submitted values.");

        console.log(
          "[FormActionsContext: updateSettings] After updateSettings, displayInCard is now:",
          submissionSettings.displayInCard
        );
      } else {
        console.warn("[FormActionsContext] No customMessage found, skipping replacement.");
      }
      break;



    case 'addToSubmissionsTable':
//      console.log(`[FormActionsContext] Adding submission to table with ID: ${action.tableId}`);
      break;

    default:
      console.warn(`[FormActionsContext] Unknown action type: ${action.type}`);
  }
};



/**
 * Converts dropdown field values (single-select & multi-select) from optionId to option label,
 * applying transformations if specified in field metadata.
 */
const transformDropdownSubmissionValues = (submissionData, fieldsMetadata, dropdownOptions) => {
  console.log("[FormActionsContext] Transforming dropdown values before submission.");

  return submissionData.map((field) => {
    const fieldMetadata = fieldsMetadata[field.id];
    if (!fieldMetadata) {
      console.warn(`[FormActionsContext] No metadata found for field "${field.id}". Skipping.`);
      return field;
    }

    const isDropdown = fieldMetadata.type === "single-select" || fieldMetadata.type === "multi-select";
    const optionsSetUFI = fieldMetadata.optionsSetUFI; // ✅ This tells us which options set to use

    if (isDropdown && optionsSetUFI && dropdownOptions[optionsSetUFI]) {
      const options = dropdownOptions[optionsSetUFI]; // ✅ Fetch options from MultiAnswerFieldContext

      // ✅ Multi-select: Map array of optionIds to corresponding transformed labels
      if (Array.isArray(field.value)) {
        const updatedValues = field.value.map((optionId) => {
          const matchedOption = options.find((opt) => opt.optionId === optionId);
          const transformedLabel = matchedOption ? applyTransformations(matchedOption.label, fieldMetadata.transformations) : optionId;

//          console.log(
//            `[FormActionsContext] Multi-select field "${field.id}" transformation applied:`,
//            `Original: "${matchedOption?.label}", Transformed: "${transformedLabel}"`
//          );

          return transformedLabel;
        });

        return { ...field, value: updatedValues };
      } else {
        // ✅ Single-select: Replace optionId with corresponding transformed label
        const matchedOption = options.find((opt) => opt.optionId === field.value);
        const transformedValue = matchedOption ? applyTransformations(matchedOption.label, fieldMetadata.transformations) : field.value;

//        console.log(
//          `[FormActionsContext] Single-select field "${field.id}" transformation applied:`,
//          `Original: "${matchedOption?.label}", Transformed: "${transformedValue}"`
//        );

        return { ...field, value: transformedValue };
      }
    }

    return field;
  });
};

// Apply per-field "Replace empty value" policy at submit-time
// IMPORTANT:
// - We apply this AFTER validation passes (so required logic still treats blank as blank).
// - We do NOT mutate values during typing / before validation.
const isTrulyBlank = (v) => {
  // type-aware emptiness (matches SubmitButtonComponent + your validation logic)
  if (Array.isArray(v)) return v.length === 0;
  if (v === null || v === undefined) return true;
  if (typeof v === 'object') return false; // attach/structured → non-null is filled
  return String(v).trim() === '';
};

const applyBlankValuePolicy = (submissionData, fieldsMetadata) => {
  return submissionData.map((field) => {
    const meta = fieldsMetadata?.[field.id];
    const policy = meta?.blankValuePolicy || null;

    // If not enabled, do nothing (default behavior)
    // Your BasicSettings toggle sets mode to 'text' when ON, and 'empty' when OFF.
    const mode = policy?.mode || 'empty';
    if (mode === 'empty') return field;

    // Only transform if the submitted value is truly blank
    if (!isTrulyBlank(field.value)) return field;

    // Current UX: toggle enables text substitution
    const fallbackText = String(policy?.text ?? '').trim() || 'null';

    return { ...field, value: fallbackText };
  });
};


const handleSubmit = async (submissionData, userData) => {
  setLoading(true);
  setSubmitStatus('');
  setError(null);
  setActionErrors([]);
  clearValidationErrors();

  console.log("--- [FormActionsContext: handleSubmit] Starting handleSubmit ---");

  await forceBlurAndWaitFrame();

  const normalizedFieldsMetadata = getNormalizedFieldsMetadata();

  if (!Object.keys(normalizedFieldsMetadata).length) {
    console.error("[FormActionsContext: handleSubmit] fieldsMetadata is still not available. Aborting submission.");
    setError("Form metadata is not loaded. Please try again.");
    setLoading(false);
    return;
  }

  console.log("[FormActionsContext: handleSubmit] fieldsMetadata loaded successfully:", normalizedFieldsMetadata);

if (!Array.isArray(submissionData) || submissionData.length === 0) {
    console.warn("[FormActionsContext: handleSubmit] Submission data is missing or undefined, constructing from context.");

    console.log("[FormActionsContext: handleSubmit] 🔍 Checking fieldChanges before constructing submissionData:", fieldChanges);
    console.log("[FormActionsContext: handleSubmit] 🔍 Checking layoutConfig before constructing submissionData:", layoutConfig);

    // ① Prefer the canonical builder from AnswersContext if available
    if (typeof buildSubmissionArray === 'function') {
      submissionData = buildSubmissionArray(); // [{ id, ufi, type, label, value, required }]
    } else {
      // ② Fallback to layout-based construction (use defaultValue, not .value)
      submissionData = Object.keys(layoutConfig).flatMap(rowKey =>
        (layoutConfig[rowKey] || []).map(field => {
          const meta = normalizedFieldsMetadata[field.id] || {};
          const fc   = fieldChanges[field.id] || {};
          // Use user-entered value if you stored it in fieldChanges.value, else metadata default
          const raw  = (fc.value !== undefined ? fc.value
                      : (fc.defaultValue !== undefined ? fc.defaultValue
                      : (meta.defaultValue !== undefined ? meta.defaultValue : '')));
          return {
            id      : field.id,
            ufi     : meta.ufi || field.id,
            type    : meta.type || field.type,
            label   : meta.label || field.label || field.id,
            value   : raw, // keep arrays/objects as-is (don’t stringify)
            required: !!(meta.required || field.required),
          };
        })
      );
    }


    submissionData = submissionData.filter(field => field.type !== 'submit');

    if (!submissionData.length) {
      console.error("[FormActionsContext: handleSubmit] No valid submission data found after filtering.");
      setError('No valid submission data found.');
      setLoading(false);
      return;
    }

    console.log("[FormActionsContext: handleSubmit] Constructed and filtered submission data:", submissionData);
  } else {
    submissionData = submissionData.filter(field => field.type !== 'submit');

    if (!submissionData.length) {
      console.error("[FormActionsContext: handleSubmit] No valid submission data found after filtering.");
      setError('No valid submission data found.');
      setLoading(false);
      return;
    }

    console.log("[FormActionsContext: handleSubmit] Filtered submission data provided to handleSubmit:", submissionData);
  }

    // Generate a unique submissionId for tracking
    const submissionId = uuidv4();
    console.log(`[FormActionsContext: handleSubmit] Generated submissionId: ${submissionId}`);


// ✅ Convert UFI to fieldId before proceeding
submissionData = submissionData.map((field) => {
  // Optional: backfill ufi if missing
  let ufi = field.ufi;
  if (!ufi) {
    const byId = normalizedFieldsMetadata[field.id];
    if (byId?.ufi) ufi = byId.ufi;
  }

  // Resolve id from ufi if possible
  const resolvedId = ufi
    ? (Object.values(normalizedFieldsMetadata).find(meta => meta.ufi === ufi)?.id || field.id)
    : field.id;

  if (!resolvedId) {
    console.warn('[FormActionsContext] Unable to resolve id/ufi for field:', field);
  }

  return { ...field, id: resolvedId, ufi: ufi || field.ufi || field.id };
});

  //  Transform dropdown values from optionId → label before further processing
  submissionData = transformDropdownSubmissionValues(submissionData, normalizedFieldsMetadata, dropdownOptions);
//  console.log("[FormActionsContext: handleSubmit] Submission data AFTER transformation (optionIds replaced with labels):", submissionData);

  // ────────────────────────────────────────────────────────────────
  // "Other" enforcement + token swap (must happen BEFORE encode)
  // Policy (enterprise best practice):
  // - if "__other__" is selected, user must provide typed text
  // - swap "__other__" → typed value so actions/table persist the real value
  // - typed value was already caps/spacing transformed live in the UI
  // ────────────────────────────────────────────────────────────────
  const OTHER_OPTION_ID = "__other__";
  const otherErrorsByFieldId = {};

  submissionData = submissionData.map((field) => {
    const meta = normalizedFieldsMetadata?.[field.id] || {};
    const allowOtherEnabled = meta?.allowOther === true;

    if (!allowOtherEnabled) return field;

    const v = field.value;

    const hasOtherSelected =
      (typeof v === 'string' && v === OTHER_OPTION_ID) ||
      (Array.isArray(v) && v.includes(OTHER_OPTION_ID));

    if (!hasOtherSelected) return field;

    const typed =
      (typeof getOtherText === 'function')
        ? String(getOtherText(field.ufi || meta.ufi || field.id) || '').trim()
        : '';

    // If Other is selected but nothing is typed, mark error (do not swap)
    if (!typed) {
      otherErrorsByFieldId[field.id] =
        meta?.otherErrorText || 'You must specify when selecting "Other".';
      return field;
    }

    // Swap token → typed value (single or multi)
    if (typeof v === 'string') {
      return { ...field, value: typed };
    }
    if (Array.isArray(v)) {
      const swapped = v.map((x) => (x === OTHER_OPTION_ID ? typed : x));
      return { ...field, value: swapped };
    }

    return field;
  });

  //  NEW: Encode fields whose metadata requires it
  submissionData = submissionData.map((field) => {
    const fieldMeta = normalizedFieldsMetadata[field.id];

    if (fieldMeta?.transformations?.encode) {
      console.log(`[FormActionsContext: handleSubmit] Encoding field "${field.id}" (label: "${field.label}") because encode=true in metadata.`);
      if (Array.isArray(field.value)) {
        field.value = field.value.map(v => (v == null ? '' : encodeURIComponent(String(v))));
      } else if (field.value && typeof field.value === 'object') {
        // If you ever encode structured values, define a policy; for now leave as-is
        field.value = field.value;
      } else {
        field.value = encodeURIComponent(field.value || "");
      }
    }
    return field;
  });





  console.log("[FormActionsContext: handleSubmit] Form ID:", formId);
  console.log("[FormActionsContext: handleSubmit] Submission data:", submissionData);
  console.log("[FormActionsContext: handleSubmit] User data:", userData);

  // Form validation before proceeding
  let isValid = true;

  // If any "Other" field is missing required typed text, mark invalid up-front
  // (we still run the loop below so errors are written consistently)
  if (otherErrorsByFieldId && Object.keys(otherErrorsByFieldId).length > 0) {
    isValid = false;
  }

  // 🔍 NEW: Debugging Validation Errors from FieldEditContext
  console.log("[FormActionsContext: handleSubmit] Fetching validationErrors from FieldEditContext:", validationErrors);

  // 🔍 NEW: Ensure validationErrors contain expected keys
  if (!validationErrors || typeof validationErrors !== 'object') {
    console.error("[FormActionsContext: handleSubmit] validationErrors is undefined or not an object. Something is wrong.");
  }

  // Check each field for validation and set errors if necessary
  submissionData.forEach((field) => {

    // "Other" required-when-selected validation
    if (otherErrorsByFieldId && otherErrorsByFieldId[field.id]) {
      console.warn(`[FormActionsContext] Validation failed for field: ${field.label} (ID: ${field.id})`);
      updateValidationError(field.id, otherErrorsByFieldId[field.id]);
      return; // skip the rest of checks for this field; it's already invalid
    }

    // type-aware emptiness (matches SubmitButtonComponent)
    const v = field.value;
    const empty = Array.isArray(v) ? v.length === 0

               : (v === null || v === undefined) ? true
               : (typeof v === 'object') ? false   // attach/structured → non-null is filled
               : String(v).trim() === '';
    const isFieldValid = !field.required || !empty;
    const fieldValidationError = validationErrors[field.id];

    console.log(`[FormActionsContext: handleSubmit] Checking validation for field: ${field.label} (ID: ${field.id})`);
    console.log(`[FormActionsContext: handleSubmit] - Required: ${field.required}`);
    console.log(`[FormActionsContext: handleSubmit] - Value: "${field.value}"`);
    console.log(`[FormActionsContext: handleSubmit] - FieldEditContext ValidationError:`, fieldValidationError);

    if (!isFieldValid || fieldValidationError) {
      console.warn(`[FormActionsContext] Validation failed for field: ${field.label} (ID: ${field.id})`);
      updateValidationError(field.id, `The field "${field.label}" is required or has errors.`);
      isValid = false;
    } else {
//      console.log(`[FormActionsContext: handleSubmit] Validation passed for field: ${field.label} (ID: ${field.id})`);
      updateValidationError(field.id, null); // Clear validation error if field is valid
    }
  });

  // Debugging Overall Validation State
  console.log("[FormActionsContext: handleSubmit] Final isValid state after validation checks:", isValid);

  if (!isValid) {
    const invalidFormError = 'Form is not valid and cannot be submitted.';
    setError(invalidFormError);
    setIsFormValid(false);
    setLoading(false);
    console.error(invalidFormError);

    if (submissionSettings.displayInCard?.enabled) {
      // setShowCard / setIsFormComplete are not provided by FormSubmissionSettingsContext in the refactor.
      // If you still need this UX, wire these setters from the correct context (or local state).
      console.warn("[FormActionsContext] displayInCard enabled but setShowCard/setIsFormComplete are not wired in this context.");
    }
    return;
  }


  setIsFormValid(true);
  console.log("[FormActionsContext: handleSubmit] Form is valid. Proceeding with submission.");

console.log('[FormActionsContext][blankPolicy] BEFORE applyBlankValuePolicy', submissionData.map(f => ({
  id: f.id,
  ufi: f.ufi,
  label: f.label,
  type: f.type,
  value: f.value,
  policy: normalizedFieldsMetadata?.[f.id]?.blankValuePolicy || null,
})));

  // Apply "Replace empty value" policy AFTER validation passes
  // This ensures required checks still operate on true blank inputs.
  submissionData = applyBlankValuePolicy(submissionData, normalizedFieldsMetadata);

// (AFTER blank policy)
console.log('[FormActionsContext][blankPolicy] AFTER applyBlankValuePolicy', submissionData.map(f => ({
  id: f.id,
  ufi: f.ufi,
  label: f.label,
  type: f.type,
  value: f.value,
})));

  /* ────────────────────────────────────────────────────────────────
   * BULK EXPANSION (reusable engine; URL CSV enabled first)
   * ----------------------------------------------------------------
   * - Detect urlField CSV (comma-separated URLs)
   * - Validate each URL
   * - Build bulkRows (cap 200)
   * - Keep submissionData as the FIRST row for backwards compatibility
   * ---------------------------------------------------------------- */
  let bulkRows = null;

  // 1) Validate CSV URLs BEFORE expanding
  if (ENABLE_BULK_URL_CSV) {
    const urlFields = (submissionData || []).filter((f) => {
      const meta = normalizedFieldsMetadata?.[f.id] || {};
      const type = meta?.type || f.type;
      return type === 'urlField' && String(f.value ?? '').includes(',');
    });

    if (urlFields.length > 0) {
      // For safety in Step 1, validate ALL urlField csv segments, but we will expand across them later.
      for (const uf of urlFields) {
        const csv = splitCsv(uf.value);
        if (!csv) continue;

        const invalid = csv.filter(u => !validateURL(u));
        if (invalid.length > 0) {
          const msg =
            `One or more URLs are invalid. Please fix: ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? '…' : ''}`;

          console.warn('[FormActionsContext][bulk:urlCsv] Validation failed:', {
            fieldId: uf.id,
            ufi: uf.ufi,
            invalidCount: invalid.length,
            invalidSample: invalid.slice(0, 10),
          });

          updateValidationError(uf.id, msg);
          setError(msg);
          setIsFormValid(false);
          setLoading(false);
          return;
        }
      }
    }
  }

  // 2) Build bulk rows (cartesian over enabled bulk sources)
  const bulkBuilt = buildBulkRows(submissionData, normalizedFieldsMetadata, submissionId);

  if (bulkBuilt && bulkBuilt.error) {
    const msg = bulkBuilt.error;
    const fieldId = bulkBuilt.errorFieldId;

    console.warn('[FormActionsContext][bulk] Cap/engine error:', {
      msg,
      fieldId,
    });

    if (fieldId) updateValidationError(fieldId, msg);
    setError(msg);
    setIsFormValid(false);
    setLoading(false);
    return;
  }

  bulkRows = bulkBuilt?.bulkRows || null;

  if (bulkRows && Array.isArray(bulkRows) && bulkRows.length > 0) {
    console.log('[FormActionsContext][bulk] bulkRows created:', {
      count: bulkRows.length,
      sample: bulkRows.slice(0, 3).map(r => ({
        rowIndex: r.rowIndex,
        clientRowId: r.clientRowId,
      })),
    });

    // Backwards compatibility: keep submissionData aligned to the first row
    submissionData = bulkRows[0]?.submissionData || submissionData;
  }

  /* ────────────────────────────────────────────────────────────────
   * ② CALCULATED FIELDS EVALUATION
   *    • Build {ufi: value} map of current answers
   *    • Run evaluator over ALL field metadata
   *    • Push each result into submissionData if not already present
   * ---------------------------------------------------------------- */
  /* ────────────────────────────────────────────────────────────────
   * ② CALCULATED FIELDS EVALUATION (row-aware)
   * ---------------------------------------------------------------- */
  const applyCalculatedFields = (rowSubmissionData) => {
    const answersByUfi = rowSubmissionData.reduce((acc, f) => {
      acc[f.ufi] = f.value;
      return acc;
    }, {});

    const calcResults = evaluateAllCalculatedFields(
      normalizedFieldsMetadata,
      answersByUfi
    );

    Object.entries(calcResults).forEach(([ufi, value]) => {
      // find the metadata for this calculated field
      const meta = Object.values(normalizedFieldsMetadata).find(
        (m) => m.ufi === ufi && m.type === FIELD_TYPE_CALCULATED
      );
      if (!meta) return; // safety

      // avoid duplicates if someone already injected this field
      if (rowSubmissionData.some((f) => f.ufi === ufi)) return;

      rowSubmissionData.push({
        // Ensure a concrete id so logs and downstream UI never see "ID: undefined"
        id:   meta?.id || meta?.ufi || `calc_${ufi}`,
        ufi,                                // calculated UFI
        label: meta.label || meta.name || 'Calculated',
        type: FIELD_TYPE_CALCULATED,
        value,
        required: false,                    // never required
      });

      console.log(
        `[FormActionsContext] Added calculated field "${meta.label}" (ufi: ${ufi}) with value:`,
        value
      );
    });

    return rowSubmissionData;
  };

  if (bulkRows && Array.isArray(bulkRows) && bulkRows.length > 0) {
    bulkRows = bulkRows.map((r) => ({
      ...r,
      submissionData: applyCalculatedFields([...(r.submissionData || [])]),
    }));

    // keep legacy single-row aligned to first row
    submissionData = bulkRows[0]?.submissionData || submissionData;
  } else {
    submissionData = applyCalculatedFields([...(submissionData || [])]);
  }

  /* ──────────────────────────────────────────────────────────────── */
  
const answersByUfiFinal = submissionData.reduce((acc, f) => {
  acc[f.ufi] = f.value;
  return acc;
}, {});
console.log('[FormActionsContext][calc] answersByUfiFinal (sample)',
  Object.entries(answersByUfiFinal).slice(0, 25)
);

  // Extract `displayInCardMessage` and push it into submissionData
  let displayInCardMessage = "";
  if (submissionSettings.displayInCard?.enabled) {
  console.log("[FormActionsContext] Replacing placeholders in displayInCardMessage before storing...");

  // 🔹 Apply transformation to replace placeholders with actual field values before storing
 // Build a robust map (ufi and id) for chip replacement
 const chipValues = submissionData.reduce((acc, field) => {
   const v = field.value ?? "";
   if (field.ufi) acc[field.ufi] = v;   // UFI key
   if (field.id)  acc[field.id]  = v;   // fieldId key
   return acc;
 }, {});

  let transformedDisplayInCardMessage = replaceChipsWithValues(
    submissionSettings.displayInCard.customMessage,
   chipValues,
    "final" // Ensure final transformation
  );


  console.log("[FormActionsContext] Final transformed displayInCardMessage (Before HTML Stripping):", transformedDisplayInCardMessage);

  // 🔹 NEW: Strip HTML Tags Before Storing
  const parser = new DOMParser();
  const doc = parser.parseFromString(transformedDisplayInCardMessage, "text/html");
  transformedDisplayInCardMessage = doc.body.textContent || "";

  console.log("[FormActionsContext] Final transformed displayInCardMessage (Plain Text):", transformedDisplayInCardMessage);

  // 🔹 Append the transformed message to submissionData
  submissionData.push({
    id: "displayInCardMessage",
    ufi: "displayInCardMessage",
    label: "Display In Card Message",
    type: "system",
    value: transformedDisplayInCardMessage, // ✅ Now stored as plain text
  });
}


try {
    if (!submissionSettings || typeof submissionSettings !== 'object') {
      console.error("[FormActionsContext] submissionSettings is undefined or not an object. Cannot process submission.");
      setError("Submission settings are missing or invalid.");
      setLoading(false);
      return null;
    }

    // Viewer safety: if we are NOT in builder and NOT in public preview,
    // do NOT execute submit actions until submissionSettings has finished resolving.
    // This prevents edge cases where a stale/previous action executes before Firestore settings land.
    const isBuilderRoute = (typeof window !== 'undefined') && window.location.pathname.startsWith('/form-builder');
    const isPublicPreviewMode = (() => {
      try {
        const raw = sessionStorage.getItem('isPublicPreview');
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed?.value === true;
      } catch (e) {
        return false;
      }
    })();

    if (!isBuilderRoute && !isPublicPreviewMode && submissionSettingsLoading) {
      console.warn("[FormActionsContext] submissionSettings still loading on viewer route. Blocking submit action execution.");
      setError("Form settings are still loading. Please try again in a moment.");
      setLoading(false);
      return null;
    }

    // Prefer activeActionType if present; fallback to scanning enabled flags
    const enabledAction =
      (activeActionType && submissionSettings[activeActionType])
        ? submissionSettings[activeActionType]
        : Object.values(submissionSettings).find(action => action && action.enabled);

    if (!enabledAction) {
      console.warn("[FormActionsContext] No enabled submission action found. Submission completed without an action.");
      setSubmitStatus("Submission completed, but no action executed.");
      setLoading(false);
    return { submissionId, submissionData, bulkRows };
    }

    console.log("[FormActionsContext: handleSubmit] Executing enabled action:", enabledAction.type);
    await handleAction(enabledAction, submissionData);
    setSubmitStatus("Submitted Successfully");
  } catch (err) {

    setError(err.message);
    console.error("[FormActionsContext] Submission error:", err);
    if (err.actionErrors) {
      setActionErrors(err.actionErrors);
      console.error("[FormActionsContext] Action errors:", err.actionErrors);
    }
  } finally {
    setLoading(false);
  }

  console.log("[FormActionsContext: handleSubmit] Returning final submission data.");
 return { submissionId, submissionData, bulkRows };

};


  const contextValue = {
    submitStatus,
    handleSubmit,
    submissions,
    loading,
    error,
    actionErrors,
  };

  return (
    <FormActionsContext.Provider value={contextValue}>
      {children}
    </FormActionsContext.Provider>
  );
};

export default FormActionsProvider;
